import { motion } from 'framer-motion';
import type { Achievement, UserAchievement } from '@/types';
import { IconByName } from '@/lib/category-icons';

interface AchievementCardProps {
  achievement: Achievement;
  unlocked?: UserAchievement;
  compact?: boolean;
}

export function AchievementCard({ achievement, unlocked, compact = false }: AchievementCardProps) {
  const isUnlocked = !!unlocked;

  const tierColors = {
    bronze: 'border-amber-700/50 bg-amber-900/10',
    silver: 'border-slate-400/50 bg-slate-600/10',
    gold: 'border-yellow-400/50 bg-yellow-500/10',
    platinum: 'border-purple-400/50 bg-purple-500/10',
  };

  const tierBadgeColors = {
    bronze: 'bg-amber-700/20 text-amber-500',
    silver: 'bg-slate-500/20 text-slate-300',
    gold: 'bg-yellow-500/20 text-yellow-400',
    platinum: 'bg-purple-500/20 text-purple-400',
  };

  if (compact) {
    return (
      <div
        className={`flex items-center gap-3 p-3 rounded-xl border ${
          isUnlocked
            ? tierColors[achievement.tier]
            : 'border-charcoal-700/30 bg-charcoal-800/30 opacity-50'
        }`}
      >
        <span className={`text-2xl ${!isUnlocked ? 'opacity-50' : ''}`}>
          <IconByName name={achievement.icon} className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isUnlocked ? 'text-white' : 'text-slate-500'}`}>
            {achievement.name}
          </p>
          <p className="text-xs text-slate-500 truncate">{achievement.description}</p>
        </div>
        {isUnlocked && (
          <span className={`text-xs px-2 py-0.5 rounded ${tierBadgeColors[achievement.tier]}`}>
            +{achievement.xpReward}
          </span>
        )}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={isUnlocked ? { scale: 1.02 } : undefined}
      className={`p-4 rounded-xl border transition-all ${
        isUnlocked
          ? `${tierColors[achievement.tier]} hover:shadow-lg`
          : 'border-charcoal-700/30 bg-charcoal-800/30'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className={`w-14 h-14 rounded-xl flex items-center justify-center ${
            isUnlocked ? 'bg-charcoal-800' : 'bg-charcoal-900 opacity-50'
          }`}
        >
          <IconByName name={achievement.icon} className="w-8 h-8" />
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-semibold ${isUnlocked ? 'text-white' : 'text-slate-500'}`}>
              {achievement.name}
            </h3>
            <span className={`text-xs px-2 py-0.5 rounded capitalize ${tierBadgeColors[achievement.tier]}`}>
              {achievement.tier}
            </span>
          </div>
          <p className={`text-sm ${isUnlocked ? 'text-slate-400' : 'text-slate-600'}`}>
            {achievement.description}
          </p>

          {/* XP Reward */}
          <div className="mt-2 flex items-center gap-3">
            <span className={`text-xs px-2 py-0.5 rounded ${
              isUnlocked ? 'bg-amber-500/20 text-amber-400' : 'bg-charcoal-700 text-slate-500'
            }`}>
              +{achievement.xpReward} XP
            </span>
            {achievement.category && (
              <span className="text-xs text-slate-500 capitalize">
                {achievement.category}
              </span>
            )}
          </div>

          {/* Unlock date */}
          {unlocked && (
            <p className="text-xs text-slate-500 mt-2">
              Unlocked {new Date(unlocked.unlockedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

interface AchievementGridProps {
  achievements: Achievement[];
  unlockedAchievements: UserAchievement[];
}

export function AchievementGrid({ achievements, unlockedAchievements }: AchievementGridProps) {
  const unlockedMap = new Map(
    unlockedAchievements.map((ua) => [ua.achievementId, ua])
  );

  // Sort: unlocked first, then by tier
  const tierOrder = { platinum: 0, gold: 1, silver: 2, bronze: 3 };
  const sorted = [...achievements].sort((a, b) => {
    const aUnlocked = unlockedMap.has(a.id);
    const bUnlocked = unlockedMap.has(b.id);
    if (aUnlocked !== bUnlocked) return aUnlocked ? -1 : 1;
    return tierOrder[a.tier] - tierOrder[b.tier];
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {sorted.map((achievement) => (
        <AchievementCard
          key={achievement.id}
          achievement={achievement}
          unlocked={unlockedMap.get(achievement.id)}
        />
      ))}
    </div>
  );
}
